/**
 * Загрузка в эффекте, чей ответ ложится ТОЛЬКО под актуальные зависимости.
 *
 * `load` зовётся на каждую смену `deps` и возвращает промис — либо ничего,
 * когда грузить нечего (товар не выбран). `done`/`fail` получают ответ, только
 * если с момента запроса эффект не перезапущен и компонент не размонтирован:
 * иначе поздний ответ прошлого товара или отбора вставал бы под шапку нового —
 * так и было в карточке товара при листании стрелками (аудит фронта
 * 12.10.26, B1–B4, B17, B58, B59).
 *
 * Тот же `let alive = true … return () => { alive = false }`, что стоит в
 * двадцати эффектах кабинета, только флаг здесь нельзя забыть: ответ без
 * проверки в состояние не попадёт.
 *
 * `done`/`fail` — ТОЛЬКО сеттеры состояния: они замкнуты рендером, в котором
 * эффект запустился, и значение состояния оттуда к приходу ответа устарело бы.
 * Сброс «старых чисел» перед запросом — внутри `load`, он идёт синхронно.
 *
 * Зависимости стоят ВТОРЫМ аргументом, как у `useEffect`: линтер
 * (`additionalHooks` в `eslint.config.js`) ищет их сразу за колбэком и сверяет
 * с телом `load` — третьим аргументом он бы их не нашёл.
 */

import { type DependencyList, useEffect } from "react";

export function useLoad<T>(
  load: () => Promise<T> | undefined,
  deps: DependencyList,
  put: { done: (data: T) => void; fail: (error: Error) => void },
): void {
  useEffect(() => {
    const promise = load();
    if (!promise) return;
    let alive = true;
    promise.then(
      (data) => {
        if (alive) put.done(data);
      },
      (error: Error) => {
        if (alive) put.fail(error);
      },
    );
    return () => {
      alive = false;
    };
    // Зависимости — вызывающего: их полноту линтер проверяет на месте вызова.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);
}
